/**
 * ============================================
 * SUCURSAL CONTEXT - Contexto de Sucursal Activa
 * ============================================
 *
 * Este contexto maneja la sucursal activa del usuario.
 * Permite cambiar entre sucursales y persiste la selección.
 *
 * Características:
 * - Usa TanStack Query para cargar sucursales (cache automático)
 * - Persiste la sucursal seleccionada en localStorage
 * - Proporciona la sucursal activa a toda la aplicación
 * - Administrador Global puede ver "Todas las sucursales"
 * - Se reinicializa automáticamente cuando el usuario cambia (login/logout)
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSucursales } from "@/hooks/use-sucursales";
import { useAuth } from "./auth-context";
import { ALL_SUCURSALES_ID, ALL_SUCURSALES, isGlobalAdmin } from "./sucursal-constants";
import { queryKeys } from "@/lib/query-client";

// Re-export constants for convenience
// eslint-disable-next-line react-refresh/only-export-components
export { ALL_SUCURSALES_ID, ALL_SUCURSALES, isGlobalAdmin } from "./sucursal-constants";

// =============================================================================
// TYPES
// =============================================================================

export interface Sucursal {
  id: number;
  codigo: string;
  nombre: string;
  direccion: string;
  telefono: string;
  is_active: boolean;
}

interface SucursalContextType {
  /** Lista de sucursales disponibles */
  sucursales: Sucursal[];
  /** Sucursal actualmente seleccionada (null = todas para admin global) */
  sucursalActiva: Sucursal | null;
  /** Indica si está cargando las sucursales */
  isLoading: boolean;
  /** Error al cargar sucursales */
  error: string | null;
  /** Cambiar la sucursal activa */
  setSucursalActiva: (sucursal: Sucursal | null) => void;
  /** Recargar lista de sucursales */
  refetch: () => Promise<void>;
  /** True si el usuario puede ver todas las sucursales (Admin Global) */
  canViewAllSucursales: boolean;
  /** True si está seleccionada "Todas las sucursales" */
  isAllSucursalesSelected: boolean;
}

// =============================================================================
// CONTEXT
// =============================================================================

const SucursalContext = createContext<SucursalContextType | undefined>(undefined);

// Key para localStorage
const STORAGE_KEY = "lims-sucursal-activa";

// =============================================================================
// PROVIDER
// =============================================================================

interface SucursalProviderProps {
  children: ReactNode;
}

export function SucursalProvider({ children }: SucursalProviderProps) {
  // =========================================================================
  // AUTH STATE - Para detectar cambios de usuario
  // =========================================================================
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  // Ref para trackear el ID del usuario anterior
  const previousUserIdRef = useRef<number | null>(null);

  // =========================================================================
  // TANSTACK QUERY - Carga de sucursales
  // =========================================================================
  const {
    data: sucursalesData,
    isLoading: isQueryLoading,
    isError,
    error: queryError,
    refetch: queryRefetch,
  } = useSucursales({ enabled: isAuthenticated });

  // =========================================================================
  // ESTADO LOCAL - Solo para la sucursal seleccionada
  // =========================================================================
  const [sucursalActiva, setSucursalActivaState] = useState<Sucursal | null>(null);
  const [canViewAllSucursales, setCanViewAllSucursales] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Sucursales con fallback a array vacío
  const sucursales = useMemo(() => sucursalesData ?? [], [sucursalesData]);

  // =========================================================================
  // EFECTO: Detectar cambio de usuario y reinicializar
  // =========================================================================
  useEffect(() => {
    const currentUserId = user?.id ?? null;
    const previousUserId = previousUserIdRef.current;

    // Si el usuario cambió (login/logout o cambio de cuenta)
    if (currentUserId !== previousUserId) {
      previousUserIdRef.current = currentUserId;

      // Si el usuario cerró sesión, limpiar estado
      if (!currentUserId) {
        setSucursalActivaState(null);
        setCanViewAllSucursales(false);
        setIsInitialized(false);
        return;
      }

      // Si el usuario inició sesión (nuevo o diferente), reinicializar
      if (currentUserId && previousUserId !== null) {
        // Invalidar cache de sucursales para forzar recarga
        queryClient.invalidateQueries({ queryKey: queryKeys.sucursales });
        setIsInitialized(false);
      }
    }
  }, [user?.id, queryClient]);

  // =========================================================================
  // EFECTO: Inicializar sucursal activa cuando los datos cargan
  // =========================================================================
  useEffect(() => {
    // Solo ejecutar cuando tenemos datos y no hemos inicializado
    if (sucursales.length === 0 || isInitialized || !isAuthenticated) return;

    const isAdmin = isGlobalAdmin(user ?? null);
    setCanViewAllSucursales(isAdmin);

    // Restaurar sucursal guardada o usar la primera
    const savedId = localStorage.getItem(STORAGE_KEY);

    if (savedId === String(ALL_SUCURSALES_ID) && isAdmin) {
      setSucursalActivaState(ALL_SUCURSALES);
    } else if (savedId) {
      const saved = sucursales.find((s) => s.id === parseInt(savedId, 10));
      if (saved) {
        setSucursalActivaState(saved);
      } else if (sucursales.length > 0) {
        setSucursalActivaState(sucursales[0]);
        localStorage.setItem(STORAGE_KEY, String(sucursales[0].id));
      }
    } else if (sucursales.length > 0) {
      if (isAdmin) {
        setSucursalActivaState(ALL_SUCURSALES);
        localStorage.setItem(STORAGE_KEY, String(ALL_SUCURSALES_ID));
      } else {
        setSucursalActivaState(sucursales[0]);
        localStorage.setItem(STORAGE_KEY, String(sucursales[0].id));
      }
    }

    setIsInitialized(true);
  }, [sucursales, isInitialized, isAuthenticated, user]);

  // =========================================================================
  // HANDLERS
  // =========================================================================

  const setSucursalActiva = useCallback((sucursal: Sucursal | null) => {
    if (sucursal) {
      setSucursalActivaState(sucursal);
      localStorage.setItem(STORAGE_KEY, String(sucursal.id));
    } else {
      setSucursalActivaState(ALL_SUCURSALES);
      localStorage.setItem(STORAGE_KEY, String(ALL_SUCURSALES_ID));
    }
  }, []);

  const refetch = useCallback(async () => {
    await queryRefetch();
  }, [queryRefetch]);

  // =========================================================================
  // VALORES DERIVADOS
  // =========================================================================

  const isAllSucursalesSelected = sucursalActiva?.id === ALL_SUCURSALES_ID;
  const error = isError ? (queryError?.message ?? "Error al cargar sucursales") : null;

  // isLoading: true mientras carga Y no hemos inicializado (solo si autenticado)
  const isLoading = isAuthenticated && (isQueryLoading || !isInitialized);

  // =========================================================================
  // CONTEXT VALUE
  // =========================================================================

  const value: SucursalContextType = {
    sucursales,
    sucursalActiva,
    isLoading,
    error,
    setSucursalActiva,
    refetch,
    canViewAllSucursales,
    isAllSucursalesSelected,
  };

  return <SucursalContext.Provider value={value}>{children}</SucursalContext.Provider>;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook para acceder al contexto de sucursal
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useSucursal(): SucursalContextType {
  const context = useContext(SucursalContext);
  if (context === undefined) {
    throw new Error("useSucursal must be used within a SucursalProvider");
  }
  return context;
}
